import * as React from "react";
import { useQuery } from "react-apollo";
import { observer } from "mobx-react";
import { ClientChatbotStore } from "src/stores/chatbotStore/ClientChatbotStore";
import { UitkLayoutFlex, UitkLayoutFlexProps, UitkLayoutFlexItemProps } from "uitk-react-layout-flex";
import { UitkHeading, UitkSubheading } from "uitk-react-text";
import {
  getLineOfBusiness,
  getFunnelLocation /* , sendAnalytics */,
} from "../CustomerNotifications/NotificationsWithDialog/NotificationQueryUtils";
import { BlossomCustomerNotification, blossomCustomerNotificationQuery, ContextInput } from "__generated__/api";
import { gql } from "apollo-boost";
import Customer = BlossomCustomerNotification.Customer;
import { ReasonsToBelieveCard } from "./ReasonsToBelieveCard";
import { Viewport, ViewSmall, ViewMedium } from "bernie-view";
import { CovidBannerVersions } from "./ReasonsToBelieveWrapper";

interface ReasonsToBelieveProps {
  inputs: {
    context: ContextInput;
    lineOfBusiness: string;
    funnelLocation: string;
    location: string;
  };
  analyticsCallback: (rfrr: string, linkname: string | null) => void;
  className?: string;
  chatbot?: ClientChatbotStore;
  version: CovidBannerVersions;
  hasSubtitle?: boolean;
}

const queryCustomerNotifications = gql`
  ${blossomCustomerNotificationQuery}
`;

export const ReasonsToBelieve = observer((props: ReasonsToBelieveProps) => {
  const queryVariables = {
    context: props.inputs.context,
    notificationLocation: props.inputs.location,
    lineOfBusiness: getLineOfBusiness(props.inputs.lineOfBusiness),
    funnelLocation: getFunnelLocation(props.inputs.funnelLocation),
  };
  const { className = "" } = props;
  const { data, error, loading } = useQuery<{ customer: Customer }>(queryCustomerNotifications, {
    variables: queryVariables,
  });

  if (error || loading || !data || !data?.customer.notification || !data.customer.notification.title) {
    return null;
  }

  const { title, links, body } = data.customer.notification;

  const heading = title?.items[0].text;
  const subHeading = title?.items[1]?.text || "";

  const getContent = (
    direction: UitkLayoutFlexProps["direction"],
    minWidth: UitkLayoutFlexItemProps["minWidth"],
    maxWidth: UitkLayoutFlexItemProps["maxWidth"]
  ) => (
    <UitkLayoutFlex className="all-y-padding-six" space="three" direction={direction} justifyContent="space-between">
      {props.version === CovidBannerVersions.ONE
        ? links?.map((link, index) => (
            <ReasonsToBelieveCard
              key={`rtb-card-${index}`}
              analyticsCallback={props.analyticsCallback}
              link={link}
              index={index}
              minWidth={minWidth}
              maxWidth={maxWidth}
              version={props.version}
            />
          ))
        : body?.map((part, index) => (
            <ReasonsToBelieveCard
              key={`rtb-card-${index}`}
              analyticsCallback={props.analyticsCallback}
              body={part}
              index={index}
              minWidth={minWidth}
              maxWidth={maxWidth}
              version={props.version}
            />
          ))}
    </UitkLayoutFlex>
  );

  return (
    <div className={`${className} all-b-margin-six`} data-testid="reasons-to-believe">
      <section className="SimpleContainer bex-homepage-module all-y-padding-three">
        <div className="all-t-padding-six">
          <UitkHeading level={2} size={700}>
            {heading}
          </UitkHeading>
          {props.hasSubtitle && <UitkSubheading>{subHeading}</UitkSubheading>}
          <Viewport>
            <ViewSmall>{getContent("column", "full_width", "full_width")}</ViewSmall>
            <ViewMedium>{getContent("row", "third_width", "third_width")}</ViewMedium>
          </Viewport>
        </div>
      </section>
    </div>
  );
});
